import type { Logger } from "@expert/logging";

import { sendErrorMetric, sendEventMetric } from "@expert/cloudwatch-metrics";
import { useEffect, useMemo } from "react";

import type { GaiaSubscriptionBody } from "../types";

import { useGaiaStore } from "../../store";
import { gaiaWsEventBus } from "../gaiaEventBus";

export const useGaiaStatusEvents = (loggerProp: Logger) => {
    const { setSubscriptionStatus, setIsWsAuthenticated, isWsAuthenticated } = useGaiaStore();
    const logger = useMemo(() => loggerProp.child({ module: "useGaiaStatusEvents" }), [loggerProp]);

    useEffect(() => {
        const gaiaEventBusAuthSuccess = gaiaWsEventBus.on("gaia_ws_authorization-success", () => {
            if (!isWsAuthenticated) {
                setIsWsAuthenticated(true);
                logger.info("GAIA | websocket authorized");

                void sendEventMetric("gaia.auth.authorized");
            }
        });
        const gaiaEventBusAuthFailure = gaiaWsEventBus.on("gaia_ws_unauthorized-connection", ({ data, timestamp }) => {
            setIsWsAuthenticated(false);
            // TODO: @Soluto-Private/expert-workspace-solve: we shoulld use fata, solve this issue with GAIA
            logger.error(
                { ...(data as unknown as object), responseTimestamp: timestamp },
                "GAIA | websocket unauthorized",
            );

            void sendErrorMetric("gaia.auth.unauthorized");
        });
        return () => {
            gaiaEventBusAuthSuccess();
            gaiaEventBusAuthFailure();
        };
    }, [setIsWsAuthenticated, isWsAuthenticated, logger]);

    useEffect(() => {
        const gaiaEventBusSubSuccess = gaiaWsEventBus.on("gaia_ws_session-subscription-success", (payload) => {
            const { sessionGroupId, sessionId } = payload.body as GaiaSubscriptionBody;
            const subscriptionKey = sessionGroupId ?? sessionId;
            if (subscriptionKey) {
                setSubscriptionStatus(subscriptionKey, "subscribed");

                const partner = useGaiaStore.getState().partners[subscriptionKey];
                const loggerWithContext = logger.child({
                    sessionId: subscriptionKey,
                    partner,
                });

                loggerWithContext.info("GAIA | session subscription confirmed");
                void sendEventMetric("gaia.subscribed");
            }
        });

        const gaiaEventBusUnsubSuccess = gaiaWsEventBus.on("gaia_ws_session-unsubscription-success", (payload) => {
            const { sessionId } = payload.body as GaiaSubscriptionBody;
            if (sessionId) {
                const partner = useGaiaStore.getState().partners[sessionId];
                const loggerWithContext = logger.child({
                    sessionId,
                    partner,
                });

                setSubscriptionStatus(sessionId, "unsubscribed");
                loggerWithContext.info("GAIA | session unsubscription confirmed");
                void sendEventMetric("gaia.unsubscribed");
            }
        });

        return () => {
            gaiaEventBusSubSuccess();
            gaiaEventBusUnsubSuccess();
        };
    }, [setSubscriptionStatus, logger]);
};
